import { useState } from "react";
import {
  OPS_OBLIGATION_CADENCES,
  OPS_OBLIGATION_KIND_LABELS,
  OPS_OBLIGATION_KINDS,
  OPS_OBLIGATION_STATUSES,
  OPS_UTILITY_SUBTYPE_LABELS,
  OPS_UTILITY_SUBTYPES,
  centsToDollarInput,
  dollarInputToCents,
  type OpsObligationCadence,
  type OpsObligationKind,
  type OpsObligationStatus,
  type OpsUtilitySubtype,
} from "@/lib/opsObligations/opsObligationFields";
import type {
  OpsObligation,
  OpsObligationAttachment,
  OpsObligationLocationChoice,
  OpsObligationWrite,
} from "@/lib/opsObligationsClient";
import { OpsObligationKindFields } from "@/components/ops/OpsObligationKindFields";
import { OpsObligationAttachmentStrip } from "@/components/ops/OpsObligationAttachmentStrip";

const fieldClass =
  "mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900";

export function OpsObligationEditor(props: {
  obligation: OpsObligation;
  locations: OpsObligationLocationChoice[];
  attachments: OpsObligationAttachment[];
  onSave: (patch: OpsObligationWrite) => Promise<void>;
  onDelete: () => Promise<void>;
  onAddFile: (file: File) => Promise<void>;
  onRemoveAttachment: (id: string) => Promise<void>;
}) {
  const o = props.obligation;
  const [kind, setKind] = useState<OpsObligationKind>(o.kind);
  const [subtype, setSubtype] = useState<OpsUtilitySubtype | "">(o.utility_subtype ?? "");
  const [locationId, setLocationId] = useState(o.location_id ?? "");
  const [provider, setProvider] = useState(o.provider_name);
  const [account, setAccount] = useState(o.account_ref);
  const [description, setDescription] = useState(o.description);
  const [paymentUrl, setPaymentUrl] = useState(o.payment_url ?? "");
  const [cadence, setCadence] = useState<OpsObligationCadence>(o.cadence);
  const [dueRule, setDueRule] = useState(o.due_rule);
  const [amount, setAmount] = useState(centsToDollarInput(o.amount_cents));
  const [responsible, setResponsible] = useState(o.responsible_party);
  const [contact, setContact] = useState(o.provider_contact);
  const [status, setStatus] = useState<OpsObligationStatus>(o.status);
  const [startsOn, setStartsOn] = useState(o.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(o.ends_on ?? "");
  const [notes, setNotes] = useState(o.notes);
  const [details, setDetails] = useState(o.details ?? {});

  function buildPatch(): OpsObligationWrite {
    return {
      kind,
      utility_subtype: kind === "utility" ? subtype || null : null,
      location_id: locationId || null,
      provider_name: provider,
      account_ref: account,
      description,
      payment_url: paymentUrl.trim() || null,
      cadence,
      due_rule: dueRule,
      amount_cents: dollarInputToCents(amount),
      responsible_party: responsible,
      provider_contact: contact,
      status,
      starts_on: startsOn || null,
      ends_on: endsOn || null,
      notes,
      details,
    };
  }

  return (
    <div className="space-y-4" data-app-panel="OpsObligationEditor">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Kind
          <select
            className={fieldClass}
            value={kind}
            onChange={(e) => {
              const next = e.target.value as OpsObligationKind;
              setKind(next);
              if (next !== "utility") setSubtype("");
            }}
          >
            {OPS_OBLIGATION_KINDS.map((k) => (
              <option key={k} value={k}>
                {OPS_OBLIGATION_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        {kind === "utility" ? (
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Utility type
            <select
              className={fieldClass}
              value={subtype}
              onChange={(e) => setSubtype(e.target.value as OpsUtilitySubtype | "")}
            >
              <option value="">(not set)</option>
              {OPS_UTILITY_SUBTYPES.map((s) => (
                <option key={s} value={s}>
                  {OPS_UTILITY_SUBTYPE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Status
            <select
              className={fieldClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as OpsObligationStatus)}
            >
              {OPS_OBLIGATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Location
          <select className={fieldClass} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Organization-wide</option>
            {props.locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        {kind === "utility" ? (
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Status
            <select
              className={fieldClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as OpsObligationStatus)}
            >
              {OPS_OBLIGATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="block text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Provider / payee
          <input className={fieldClass} value={provider} onChange={(e) => setProvider(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Account / reference
          <input className={fieldClass} value={account} onChange={(e) => setAccount(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Cadence
          <select
            className={fieldClass}
            value={cadence}
            onChange={(e) => setCadence(e.target.value as OpsObligationCadence)}
          >
            {OPS_OBLIGATION_CADENCES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Due rule
          <input
            className={fieldClass}
            value={dueRule}
            onChange={(e) => setDueRule(e.target.value)}
            placeholder="15th of each month"
          />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Amount (USD)
          <input className={fieldClass} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Payment URL
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              className={`min-w-0 flex-1 ${fieldClass} mt-0`}
              value={paymentUrl}
              onChange={(e) => setPaymentUrl(e.target.value)}
              placeholder="https://"
            />
            {/^https?:\/\//i.test(paymentUrl.trim()) ? (
              <a
                className="shrink-0 text-sm text-blue-700 underline dark:text-blue-300"
                href={paymentUrl.trim()}
                target="_blank"
                rel="noreferrer"
              >
                Open portal
              </a>
            ) : null}
          </div>
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Starts
          <input className={fieldClass} type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Ends / expires
          <input className={fieldClass} type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Responsible party
          <input className={fieldClass} value={responsible} onChange={(e) => setResponsible(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Provider contact
          <input className={fieldClass} value={contact} onChange={(e) => setContact(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Description
          <textarea className={fieldClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Notes
          <textarea className={fieldClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      <OpsObligationKindFields kind={kind} details={details} onChange={setDetails} />
      <OpsObligationAttachmentStrip
        attachments={props.attachments}
        onAddFile={props.onAddFile}
        onRemove={props.onRemoveAttachment}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white dark:bg-zinc-200 dark:text-zinc-900"
          onClick={() => void props.onSave(buildPatch())}
        >
          Save
        </button>
        <button
          type="button"
          className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 dark:border-red-700 dark:text-red-300"
          onClick={() => void props.onDelete()}
        >
          {o.status === "draft" ? "Delete draft" : "Mark ended"}
        </button>
      </div>
    </div>
  );
}
